import { useMutation, useQueryClient } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';

import { disconnectStripe } from '../api/connect';
import { StripeIcon } from '../StripeIcon';
import type { StripeConnection, StripeConnectionStatus } from '../types';
import { ConnectStripeButton } from './ConnectStripeButton';

interface StatusMeta {
  color: 'success' | 'blue' | 'error' | 'gray';
  label: string;
}

const STATUS_META: Record<StripeConnectionStatus, StatusMeta> = {
  connected: { color: 'success', label: 'Connected' },
  syncing: { color: 'blue', label: 'Syncing…' },
  disconnected: { color: 'gray', label: 'Disconnected' },
};

interface StripeAccountCardProps {
  orgId: number | string;
  connection: StripeConnection;
  canManage: boolean;
  stripeUnavailable: boolean;
  onReconnectError?: (reason: string) => void;
}

export const StripeAccountCard = memo(function StripeAccountCard({
  orgId,
  connection,
  canManage,
  stripeUnavailable,
  onReconnectError,
}: StripeAccountCardProps) {
  const queryClient = useQueryClient();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);

  const { mutate, reset, isPending, isError } = useMutation({
    mutationFn: () => disconnectStripe(orgId, connection.connection_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['stripeConnections', orgId],
      });
      setIsDisconnectOpen(false);
    },
  });

  const handleDisconnectClick = useCallback(() => {
    // Clear any error from a previous failed attempt so the dialog opens clean.
    reset();
    setIsDisconnectOpen(true);
  }, [reset]);

  const handleConfirmDisconnect = useCallback(() => {
    mutate();
  }, [mutate]);

  const handleCancelDisconnect = useCallback(() => {
    setIsDisconnectOpen(false);
    reset();
  }, [reset]);

  const { connection_status: status } = connection;
  const meta = STATUS_META[status];
  const title = connection.display_name ?? connection.account_id;

  const showDisconnect =
    canManage && (status === 'connected' || status === 'syncing');
  const isReconnectable = status === 'disconnected';
  const showReconnect = canManage && !stripeUnavailable && isReconnectable;
  // When an existing connection needs reconnecting but Stripe is unconfigured,
  // the Reconnect button is hidden — explain why so the card isn't a dead end.
  const showReconnectUnavailable =
    canManage && stripeUnavailable && isReconnectable;

  return (
    <div className='rounded-xl border border-[var(--color-border-secondary)] p-6'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex items-start gap-3'>
          <StripeIcon className='mt-0.5 h-5 w-5 shrink-0' />
          <div>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='font-medium text-[var(--color-text-primary)]'>
                {title}
              </span>
              <Badge type='color' size='sm' color={meta.color}>
                {meta.label}
              </Badge>
            </div>
            {status === 'syncing' && (
              <p
                role='status'
                className='mt-1 text-sm text-[var(--color-text-secondary)]'
              >
                Importing your Stripe data…
              </p>
            )}
            {status === 'connected' && connection.last_sync_time && (
              <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
                Last synced {connection.last_sync_time}
              </p>
            )}
            {status === 'disconnected' && (
              <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
                This account is disconnected.
              </p>
            )}
            {showReconnectUnavailable && (
              <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
                Reconnecting is unavailable while the Stripe integration is not
                configured.
              </p>
            )}
          </div>
        </div>

        {(showDisconnect || showReconnect) && (
          <div className='shrink-0'>
            {showDisconnect && (
              <Button
                color='secondary'
                size='sm'
                onClick={handleDisconnectClick}
              >
                Disconnect
              </Button>
            )}
            {showReconnect && (
              // Reuse the connect flow: the authorize endpoint returns JSON
              // ({ url }) — it does not redirect, so a plain link/navigation to
              // it would render raw JSON instead of reaching Stripe.
              <ConnectStripeButton
                orgId={orgId}
                label='Reconnect'
                size='sm'
                onError={onReconnectError}
              />
            )}
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={isDisconnectOpen}
        variant='destructive'
        title='Disconnect Stripe?'
        description='Syncing for this account stops immediately. Stripe data already imported into Ahoy stays – reconnecting is quick.'
        confirmLabel='Disconnect'
        isLoading={isPending}
        onConfirm={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
        instance='disconnect-stripe'
      >
        {isError && (
          <p
            role='alert'
            className='mt-3 text-sm text-[var(--color-text-error-primary)]'
          >
            Couldn&apos;t disconnect. Please try again.
          </p>
        )}
      </ConfirmationDialog>
    </div>
  );
});
